import React, { useState } from 'react';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';

/**
 * CustomerCreationStep - Step 1: Create a new customer in IRIS.
 */
const CustomerCreationStep = ({ wizard, api }) => {
  const [formData, setFormData] = useState({
    customerName: '',
    customerCode: '',
    organizationDetails: ''
  });
  const [result, setResult] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  const validate = () => {
    const errors = {};
    if (!formData.customerName.trim()) errors.customerName = 'Customer name is required';
    if (formData.customerName.length > 500) errors.customerName = 'Maximum 500 characters';
    if (!formData.customerCode.trim()) errors.customerCode = 'Customer code is required';
    if (formData.customerCode.length > 20) errors.customerCode = 'Maximum 20 characters';
    if (formData.organizationDetails && formData.organizationDetails.length > 500) {
      errors.organizationDetails = 'Maximum 500 characters';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const customer = await api.createCustomer(wizard.sessionId, formData);
      setResult(customer);
      wizard.setCustomerId(customer.id);
      wizard.completeStep(1);
    } catch (err) {
      // Error is handled by useProvisioningApi hook
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors(prev => { const e = { ...prev }; delete e[field]; return e; });
    }
  };

  if (result) {
    return (
      <Alert variant="success">
        <Alert.Heading>Customer Created</Alert.Heading>
        <p><strong>Name:</strong> {result.customerName}</p>
        <p><strong>Code:</strong> {result.customerCode}</p>
        <p><strong>ID:</strong> <code>{result.id}</code></p>
        <p className="text-muted mb-0">Click "Next" to continue to ruleset configuration.</p>
      </Alert>
    );
  }

  return (
    <div>
      <h5>Step 1: Create Customer</h5>
      <p className="text-muted">Enter the new customer details. This will create the customer record in IRIS.</p>

      {api.error && <Alert variant="danger" dismissible onClose={api.clearError}>{api.error}</Alert>}

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Customer Name *</Form.Label>
          <Form.Control
            type="text"
            value={formData.customerName}
            onChange={(e) => handleChange('customerName', e.target.value)}
            isInvalid={!!validationErrors.customerName}
            maxLength={500}
            disabled={api.loading}
          />
          <Form.Control.Feedback type="invalid">{validationErrors.customerName}</Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Customer Code *</Form.Label>
          <Form.Control
            type="text"
            value={formData.customerCode}
            onChange={(e) => handleChange('customerCode', e.target.value)}
            isInvalid={!!validationErrors.customerCode}
            maxLength={20}
            disabled={api.loading}
          />
          <Form.Control.Feedback type="invalid">{validationErrors.customerCode}</Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Organization Details</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={formData.organizationDetails}
            onChange={(e) => handleChange('organizationDetails', e.target.value)}
            isInvalid={!!validationErrors.organizationDetails}
            maxLength={500}
            disabled={api.loading}
          />
          <Form.Control.Feedback type="invalid">{validationErrors.organizationDetails}</Form.Control.Feedback>
        </Form.Group>

        <Button type="submit" variant="primary" disabled={api.loading}>
          {api.loading ? <><Spinner size="sm" className="me-2" />Creating...</> : 'Create Customer'}
        </Button>
      </Form>
    </div>
  );
};

export default CustomerCreationStep;
